import PropTypes from 'prop-types';

import { CAPACITY_UNITS } from './tea';
import { Field } from './fields';

// Economics tab. Product keys come from the caller's outputSlugs so the
// fields registered here match the keys buildTeaPayload reads back.

const TEASection = ({
  register, setValue, watchedTea, watchedOutputs, outputSlugs, outputUnits,
}) => {
  const mode = watchedTea?.mode || 'none';
  // Reference product list excludes emissions outputs — they're not
  // physically meaningful products to price.
  const productOptions = outputSlugs
    .map((slug, idx) => ({
      slug,
      unit: outputUnits[idx],
      isEmissions: !!watchedOutputs?.[idx]?.emissions,
      isAllocatable: watchedOutputs?.[idx]?.allocatable !== false,
    }))
    .filter(o => !o.isEmissions && o.isAllocatable);
  const refUnit = productOptions.find(o => o.slug === watchedTea?.reference_product)?.unit ?? '<output>';

  return (
    <div className="mb-4 text-xs">
      {/* Mode selector --------------------------------------------- */}
      <div className="bg-gray-50 border border-gray-200 rounded p-3 mb-3">
        <div className="font-medium text-sm mb-2">TEA mode</div>
        <div className="flex gap-4">
          <ModeRadio register={register} value="none" label="None" hint="No economics; module produces emissions/flow data only." />
          <ModeRadio register={register} value="simple" label="Simple" hint="Set a unit cost per product output ($/<unit>)." />
          <ModeRadio register={register} value="dcfa" label="DCFA" hint="Full discounted cash-flow analysis with capex, opex, scaling." />
        </div>
      </div>

      {mode === 'simple' && (
        <SimpleTEAForm
          register={register}
          productOptions={productOptions}
        />
      )}

      {mode === 'dcfa' && (
        <DCFATEAForm
          register={register}
          setValue={setValue}
          watchedTea={watchedTea}
          productOptions={productOptions}
          refUnit={refUnit}
        />
      )}
    </div>
  );
};

TEASection.propTypes = {
  register: PropTypes.func.isRequired,
  setValue: PropTypes.func.isRequired,
  watchedTea: PropTypes.object,
  watchedOutputs: PropTypes.array,
  outputSlugs: PropTypes.arrayOf(PropTypes.string).isRequired,
  outputUnits: PropTypes.arrayOf(PropTypes.string).isRequired,
};

const ModeRadio = ({ register, value, label, hint }) => (
  <label className="flex items-start gap-2 cursor-pointer">
    <input type="radio" className="radio radio-xs mt-0.5" value={value} {...register('tea.mode')} />
    <span>
      <span className="font-medium">{label}</span>
      <span className="block text-gray-500 text-[11px]">{hint}</span>
    </span>
  </label>
);

ModeRadio.propTypes = {
  register: PropTypes.func.isRequired,
  value: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  hint: PropTypes.string.isRequired,
};

const SimpleTEAForm = ({ register, productOptions }) => (
  <div className="bg-gray-50 border border-gray-200 rounded p-3">
    <div className="font-medium text-sm mb-2">Product unit costs</div>
    {productOptions.length === 0 ? (
      <p className="text-gray-500">No non-emissions outputs declared.</p>
    ) : (
      <div className="space-y-2">
        {productOptions.map(({ slug, unit }) => (
          <div key={slug} className="flex items-center gap-2">
            <span className="w-32 font-mono">{slug}</span>
            <input
              type="number" step="any" min="0"
              className="input input-xs input-bordered w-32"
              {...register(`tea.simple_costs.${slug}`)}
              placeholder="0.00"
            />
            <span className="text-gray-500 text-[11px]">$ / {unit}</span>
          </div>
        ))}
      </div>
    )}
  </div>
);

SimpleTEAForm.propTypes = {
  register: PropTypes.func.isRequired,
  productOptions: PropTypes.array.isRequired,
};

const DCFATEAForm = ({ register, setValue, watchedTea, productOptions, refUnit }) => (
  <div className="bg-gray-50 border border-gray-200 rounded p-3">
    <div className="grid grid-cols-2 gap-x-4 gap-y-2">
      <Field label="Reference product">
        <select className="select select-xs select-bordered w-full" {...register('tea.reference_product')}>
          <option value="">— pick one —</option>
          {productOptions.map(o => (
            <option key={o.slug} value={o.slug}>{o.slug}</option>
          ))}
        </select>
      </Field>
      <Field label="Reference capacity">
        <div className="flex gap-1">
          <input
            type="number" step="any"
            className="input input-xs input-bordered flex-1"
            {...register('tea.reference_capacity')}
          />
          <select
            className="select select-xs select-bordered w-24"
            {...register('tea.reference_capacity_unit')}
          >
            {CAPACITY_UNITS.map(u => (
              <option key={u.token} value={u.token}>{u.label}</option>
            ))}
          </select>
        </div>
      </Field>
      <Field label="Capacity factor (0–1)">
        <input
          type="number" step="any" min="0" max="1"
          className="input input-xs input-bordered w-full"
          {...register('tea.capacity_factor')}
        />
      </Field>
      <Field
        label="Scaling exponent"
        tooltip={
          'Power-law exponent for capex/opex scaling: cost ∝ (capacity / reference_capacity) ^ exponent. ' +
          'Set to 0 (default) to disable scaling — entered costs apply as-is regardless of capacity. ' +
          'Typical industrial values are 0.6–0.7 (six-tenths rule).'
        }
      >
        <input
          type="number" step="any"
          className="input input-xs input-bordered w-full"
          {...register('tea.scaling_exponent')}
          placeholder="0"
        />
      </Field>
      <Field label="Capital cost ($)">
        <MoneyInput name="tea.capital_cost" value={watchedTea?.capital_cost} setValue={setValue} />
      </Field>
      <Field label="Variable operating cost">
        <div className="flex gap-1">
          <input
            type="number" step="any" min="0"
            className="input input-xs input-bordered flex-1"
            {...register('tea.variable_operating_costs')}
          />
          <span className="text-gray-500 text-[10px] self-center whitespace-nowrap">
            $ / {refUnit}
          </span>
        </div>
      </Field>
      <Field label="Fixed operating cost ($/year)">
        <MoneyInput name="tea.fixed_operating_costs" value={watchedTea?.fixed_operating_costs} setValue={setValue} />
      </Field>
      <Field label="Fixed maintenance cost ($/year)">
        <MoneyInput name="tea.fixed_maintenance_costs" value={watchedTea?.fixed_maintenance_costs} setValue={setValue} />
      </Field>
    </div>
  </div>
);

DCFATEAForm.propTypes = {
  register: PropTypes.func.isRequired,
  setValue: PropTypes.func.isRequired,
  watchedTea: PropTypes.object,
  productOptions: PropTypes.array.isRequired,
  refUnit: PropTypes.string.isRequired,
};

// Money input with thousands separators. We display the formatted string
// in a text input and write the raw number back to the form on every
// change so submit sees a clean numeric value.
const MoneyInput = ({ name, value, setValue }) => {
  const formatted = formatMoney(value);
  return (
    <input
      type="text"
      inputMode="decimal"
      className="input input-xs input-bordered w-full font-mono"
      value={formatted}
      onChange={e => {
        const raw = e.target.value.replace(/,/g, '');
        const num = parseFloat(raw);
        setValue(name, Number.isFinite(num) ? num : 0, { shouldDirty: true });
      }}
    />
  );
};

MoneyInput.propTypes = {
  name: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  setValue: PropTypes.func.isRequired,
};

const _moneyFormatter = new Intl.NumberFormat('en-US', { maximumFractionDigits: 2 });
function formatMoney(value) {
  if (value === '' || value === null || value === undefined) return '';
  const num = typeof value === 'number' ? value : parseFloat(value);
  if (!Number.isFinite(num)) return '';
  return _moneyFormatter.format(num);
}

export default TEASection;
